import { useEffect, useRef, useState } from 'react'
import Hls from 'hls.js'
import { X } from 'lucide-react'
import { QuizPreviewPanel } from './QuizPreviewPanel'

interface PauseAndGoItem {
  id: number
  time: number
  type: string
  published: boolean
  isBlockQuiz: boolean
  quizId: string | null
}

interface Props {
  open: boolean
  onClose: () => void
  hlsUrl: string | null
  pauseAndGos: PauseAndGoItem[]
  initialSelectedId?: number | null
  lessonTitle?: string
}

function formatDuration(ms: number | null | undefined): string {
  if (!ms) return '-'
  const totalSeconds = Math.floor(ms / 1000)
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export function PauseAndGoExperienceModal({
  open,
  onClose,
  hlsUrl,
  pauseAndGos,
  initialSelectedId,
  lessonTitle,
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const hlsRef = useRef<Hls | null>(null)
  const blockQuizzes = pauseAndGos.filter(p => p.isBlockQuiz && p.quizId)
  const [selectedId, setSelectedId] = useState<number | null>(null)

  // 모달 열릴 때 초기 선택 결정
  useEffect(() => {
    if (!open) return
    if (initialSelectedId != null && blockQuizzes.some(p => p.id === initialSelectedId)) {
      setSelectedId(initialSelectedId)
    } else if (blockQuizzes.length > 0) {
      setSelectedId(blockQuizzes[0].id)
    } else {
      setSelectedId(null)
    }

  }, [open, initialSelectedId])

  // HLS attach / detach
  useEffect(() => {
    if (!open || !hlsUrl || !videoRef.current) return
    const video = videoRef.current
    if (Hls.isSupported()) {
      const hls = new Hls()
      hls.loadSource(hlsUrl)
      hls.attachMedia(video)
      hlsRef.current = hls
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl
    }
    return () => {
      hlsRef.current?.destroy()
      hlsRef.current = null
    }
  }, [open, hlsUrl])

  // 선택 변경 시 영상 seek
  const selected = blockQuizzes.find(p => p.id === selectedId) ?? null
  useEffect(() => {
    if (!open || !selected || !videoRef.current) return
    videoRef.current.currentTime = selected.time / 1000
    videoRef.current.play().catch(() => {/* autoplay 거부는 무시 */})
  }, [open, selected])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-[92vh] flex flex-col">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Pause &amp; Go 체험 {lessonTitle && <span className="text-slate-500 font-normal">— {lessonTitle}</span>}
            </h2>
            <p className="text-xs text-slate-500">블록퀴즈 {blockQuizzes.length}개</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </header>
        <div className="flex-1 grid grid-cols-2 min-h-0">
          {/* Left: video */}
          <div className="bg-black flex items-center justify-center">
            {hlsUrl ? (
              <video ref={videoRef} controls className="w-full h-full" />
            ) : (
              <div className="text-sm text-slate-400">영상 없음</div>
            )}
          </div>
          {/* Right: list + preview */}
          <div className="flex flex-col min-h-0 border-l border-slate-200">
            <div className="p-3 border-b border-slate-200 overflow-y-auto max-h-[40%]">
              {blockQuizzes.length === 0 ? (
                <div className="py-6 text-sm text-center text-slate-400">블록퀴즈가 없습니다</div>
              ) : (
                <ul className="space-y-1">
                  {blockQuizzes.map((p, i) => {
                    const active = p.id === selectedId
                    return (
                      <li key={p.id}>
                        <button
                          onClick={() => setSelectedId(p.id)}
                          className={
                            'w-full text-left px-3 py-2 rounded-md text-xs flex items-center gap-3 transition-colors ' +
                            (active
                              ? 'bg-amber-50 text-amber-900 ring-1 ring-amber-300'
                              : 'hover:bg-slate-50 text-slate-700')
                          }
                        >
                          <span className="text-slate-400 font-mono w-5">{i + 1}.</span>
                          <span className="font-mono text-slate-600 w-16">{formatDuration(p.time)}</span>
                          <span className="font-mono text-slate-500 flex-1 truncate">{p.quizId}</span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {selected?.quizId ? (
                <QuizPreviewPanel key={selected.quizId} quizId={selected.quizId} />
              ) : (
                <div className="py-10 text-sm text-center text-slate-400">위에서 블록퀴즈를 선택하세요</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
